import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiHeart, FiUsers, FiAward } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const CultureSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    // Parallax for the image
    gsap.to(imageRef.current, {
      y: "-15%",
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });

    tl.from(imageRef.current, {
      opacity: 0,
      x: -100,
      duration: 1.2,
      ease: "power3.out",
    }).from(
      section.querySelectorAll(".content-animate"),
      {
        opacity: 0,
        y: 50,
        stagger: 0.2,
        duration: 0.8,
        ease: "power2.out",
      },
      "-=0.8"
    );

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const culturePoints = [
    {
      icon: <FiHeart className="text-accent h-8 w-8" />,
      title: "Community",
      description: "We thrive on collective support and shared joy.",
    },
    {
      icon: <FiUsers className="text-accent h-8 w-8" />,
      title: "Family",
      description: "Kinship extends beyond bloodlines to every neighbor.",
    },
    {
      icon: <FiAward className="text-accent h-8 w-8" />,
      title: "Tradition",
      description: "Our heritage is the drumbeat of our daily lives.",
    },
  ];

  return (
    <section ref={sectionRef} className="overflow-hidden bg-white">
      <div className="mx-auto flex max-w-7xl flex-col items-center md:flex-row">
        {/* Left Side: Image */}
        <div className="h-[30rem] w-full overflow-hidden md:h-[40rem] md:w-[45%]">
          <div
            ref={imageRef}
            className="h-full w-full bg-cover bg-center"
            style={{
              backgroundImage: `url(${require("assets/img/places/place2.png")})`,
            }}
          ></div>
        </div>

        {/* Right Side: Content */}
        <div className="w-full p-8 md:w-[55%] md:p-16 lg:p-20">
          <h2 className="content-animate font-serif text-4xl font-semibold leading-tight text-nature-green-dark md:text-5xl">
            A Tapestry of Joy and Togetherness
          </h2>
          <p className="content-animate mt-6 text-lg leading-relaxed text-gray-700">
            Nunya, Isuikwuato LGA, Abia State, Nigeria, is a vibrant mosaic of
            shared laughter, collective support, and deep-rooted traditions.
            Here, every face is familiar, every door is open, and the spirit of
            'ubuntu'—I am because we are—guides our way of life. We celebrate
            together, mourn together, and build together, creating a place that
            is more like a large, extended family.
          </p>
          <div className="content-animate mt-12 grid grid-cols-1 gap-8 text-center sm:grid-cols-3">
            {culturePoints.map((point, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="rounded-full bg-complementary/30 p-4">
                  {point.icon}
                </div>
                <h3 className="mt-4 text-xl font-bold text-gray-800">
                  {point.title}
                </h3>
                <p className="mt-1 text-gray-600">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="from-transparent to-transparent mt-10 h-px w-full bg-gradient-to-r via-amber-300"></div>
    </section>
  );
};

export default CultureSection;
